@if ((projectAssignments$ | async) === null || (project$ | async) === null) {
  <div class="overview-loader">
    <div class="overview-loader-spinner"></div>
    <span>{{ "MESSAGES.LOADING" | translate }}</span>
  </div>
}

@if (project$ | async; as project) {
  <div class="project-overview-page">
    <app-header
      [title]="project.title"
      [subtitle]="'COMPONENTS.PROJECT_OVERVIEW.SUBTITLE' | translate"
    >
      @if (project.imageUrl !== notFound) {
        <div header-title-after class="overview-header-cover overview-header-cover--inline">
          <img [src]="project.imageUrl" alt="" />
        </div>
      } @else {
        <div
          header-title-after
          class="overview-header-cover overview-header-cover--fallback overview-header-cover--inline"
          [ngStyle]="{ backgroundColor: project.color }"
        >
          <span>{{ project.title | abbreviation }}</span>
        </div>
      }

      <div header-actions class="hidden lg:block">
        <app-navbar-profile-info />
      </div>
    </app-header>

    <div class="project-overview-content py-6 md:py-8">
      <div class="overview-toolbar">
        <app-back-button [fallbackUrl]="projectsBackUrl" />

        <div class="overview-toolbar-right">
          @if (canCreateAssignment$ | async) {
            @if (!project.isArchived) {
              <button
                type="button"
                class="overview-btn overview-btn-primary"
                (click)="openCreateAssignmentModal()"
              >
                + {{ "BUTTONS.ADD_ASSIGNMENT" | translate }}
              </button>
            }
          }

          <button
            type="button"
            class="overview-btn overview-btn-secondary overview-files-trigger"
            (click)="openFilesModal()"
          >
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3.333 5.833A1.667 1.667 0 0 1 5 4.167h3.333l1.667 1.666h5A1.667 1.667 0 0 1 16.667 7.5v7.5A1.667 1.667 0 0 1 15 16.667H5A1.667 1.667 0 0 1 3.333 15V5.833Z"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linejoin="round"
              />
            </svg>
            {{ "BUTTONS.FILES" | translate }}
          </button>
        </div>
      </div>

      @if (project.isArchived) {
        <p class="overview-archived-note">
          {{ "MESSAGES.ARCHIVED_PROJECT" | translate }}
        </p>
      }

      <section class="project-summary-card">
        <div class="project-summary-main">
          <div class="project-summary-info">
            @if (project.description) {
              <p class="project-summary-description">{{ project.description }}</p>
            }

            <div class="project-team-block">
              <span class="project-team-label">
                {{ "TABLE_HEADERS.TEAM" | translate }}
              </span>

              <div class="project-team-stack-row">
                <div class="project-team-stack">
                  @for (profile of teamPreviewMembers; track profile.id) {
                    <div
                      class="project-team-avatar"
                      [class.project-team-avatar--placeholder]="profile.imageUrl === notFound"
                      [ngbTooltip]="profile.fullName"
                      placement="top"
                    >
                      @if (profile.imageUrl !== notFound) {
                        <img [src]="profile.imageUrl" [alt]="profile.fullName" />
                      } @else {
                        <span>{{ profile.fullName | abbreviation }}</span>
                      }
                    </div>
                  }
                </div>

                @if (teamOverflowMembers.length > 0) {
                  <div class="project-team-dropdown" ngbDropdown placement="bottom-start">
                    <button
                      type="button"
                      class="project-team-dropdown-trigger"
                      ngbDropdownToggle
                    >
                      +{{ teamOverflowMembers.length }}
                    </button>

                    <div ngbDropdownMenu class="project-team-dropdown-menu">
                      <div class="project-team-dropdown-title">
                        {{ "BUTTONS.WHOLE_LIST" | translate }}
                      </div>

                      @for (profile of teamOverflowMembers; track profile.id) {
                        <button type="button" ngbDropdownItem class="project-team-dropdown-item">
                          <app-minimal-profile-info
                            [profile]="profile"
                            imageWidth="28px"
                            imageHeight="28px"
                            fontSize="11px"
                            [collapseDetailsOnMobile]="false"
                          />
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>

            </div>
          </div>
          @if (canOpenProjectActions$ | async) {
            @if (canEditProject$ | async) {
              @if (canDeleteProject$ | async) {
                <app-action-menu
                  [items]="project.isArchived ? projectActionItemsArchived : projectActionItemsActive"
                  (actionSelected)="handleProjectAction($event, project)"
                />
              } @else {
                <app-action-menu
                  [items]="project.isArchived ? projectActionItemsEditOnlyArchived : projectActionItemsEditOnlyActive"
                  (actionSelected)="handleProjectAction($event, project)"
                />
              }
            } @else if (canDeleteProject$ | async) {
              <app-action-menu
                [items]="projectActionItemsDeleteOnly"
                (actionSelected)="handleProjectAction($event, project)"
              />
            }
          }
        </div>
      </section>

      <form [formGroup]="filterForm.form" class="overview-filter-card">
        <div class="filter-statuses">
          @for (option of assignmentFilterOptions; track option.controlName) {
            <label
              [for]="option.controlName"
              class="filter-chip"
              [ngClass]="[
                option.chipClass,
                filterForm.form.controls[option.controlName].value
                  ? 'filter-chip--checked'
                  : ''
              ]"
            >
              <input
                type="checkbox"
                [formControlName]="option.controlName"
                [name]="option.controlName"
                [id]="option.controlName"
                [value]="option.status"
              />
              <span class="filter-chip-dot"></span>
              <span>{{ option.translationKey | translate }}</span>
            </label>
          }
        </div>

        <div class="filter-controls">
          <label
            for="isArchived"
            class="filter-chip filter-chip--archived"
            [class.filter-chip--checked]="filterForm.form.controls['isArchived'].value"
          >
            <input
              type="checkbox"
              formControlName="isArchived"
              name="isArchived"
              id="isArchived"
            />
            <span class="filter-chip-dot"></span>
            <span>{{ "ASSIGNMENT_STATUSES_PLURAL.ARCHIVED" | translate }}</span>
          </label>

          <div class="filter-sort">
            <label for="sort-select" class="filter-sort-select-wrapper">
              <select
                id="sort-select"
                class="filter-sort-select"
                [formControlName]="filterForm.fieldNames.sortColumn"
              >
                <option ngValue="created_at">
                  {{ "ASSIGNMENT_SORT.BY_CREATED_DATE" | translate }}
                </option>
                <option ngValue="deadline">
                  {{ "ASSIGNMENT_SORT.BY_DEADLINE" | translate }}
                </option>
                <option ngValue="user">
                  {{ "ASSIGNMENT_SORT.BY_PERFORMER" | translate }}
                </option>
                <option ngValue="priority">
                  {{ "ASSIGNMENT_SORT.BY_PRIORITY" | translate }}
                </option>
              </select>
            </label>

            <label
              role="button"
              for="sortOrder"
              class="filter-sort-order"
              [class.filter-sort-order--asc]="!filterForm.form.controls['sortOrder'].value"
              [ngbTooltip]="
                filterForm.form.controls['sortOrder'].value
                  ? ('ASSIGNMENT_SORT.ORDER_DESC' | translate)
                  : ('ASSIGNMENT_SORT.ORDER_ASC' | translate)
              "
              placement="top"
            >
              <input
                type="checkbox"
                [formControlName]="filterForm.fieldNames.sortOrder"
                name="sortOrder"
                id="sortOrder"
                hidden
              />
              <svg viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.70925 6.54C5.61628 6.44627 5.50568 6.37188 5.38382 6.32111C5.26196 6.27034 5.13126 6.2442 4.99925 6.2442C4.86723 6.2442 4.73653 6.27034 4.61467 6.32111C4.49281 6.37188 4.38221 6.44627 4.28925 6.54L1.28925 9.54C1.10094 9.7283 0.995155 9.9837 0.995155 10.25C0.995155 10.5163 1.10094 10.7717 1.28925 10.96C1.47755 11.1483 1.73294 11.2541 1.99925 11.2541C2.26555 11.2541 2.52094 11.1483 2.70925 10.96L4.99925 8.66L7.28925 10.96C7.48285 11.1483 7.74334 11.252 8.01339 11.2482C8.28344 11.2445 8.54094 11.1336 8.72925 10.94C8.91755 10.7464 9.02123 10.4859 9.01748 10.2159C9.01373 9.9458 8.90285 9.6883 8.70925 9.5L5.70925 6.54ZM2.70925 5.46L4.99925 3.16L7.28925 5.46C7.48285 5.6483 7.74334 5.75198 8.01339 5.74823C8.28344 5.74448 8.54094 5.63361 8.72925 5.44C8.91755 5.24639 9.02123 4.98591 9.01748 4.71586C9.01373 4.4458 8.90285 4.1883 8.70925 4L5.70925 0.999998C5.61628 0.90627 5.50568 0.831875 5.38382 0.781107C5.26196 0.730338 5.13126 0.704198 4.99925 0.704198C4.86724 0.704198 4.73653 0.730338 4.61467 0.781107C4.49281 0.831875 4.38221 0.90627 4.28925 0.999998L1.28925 4C1.10094 4.1883 0.995155 4.4437 0.995155 4.71C0.995155 4.9763 1.10094 5.23169 1.28925 5.42C1.47755 5.6083 1.73294 5.71409 1.99925 5.71409C2.26555 5.71409 2.52094 5.6083 2.70925 5.42V5.46Z"
                  fill="currentColor"
                />
              </svg>
            </label>
          </div>
        </div>
      </form>

      @if ((projectAssignments$ | async)?.totalCount === 0) {
        <div class="overview-empty-state">
          <app-empty-content
            [showImage]="true"
            [message]="
              'MESSAGES.EMPTY_LIST'
                | translate: { param: emptyAssignmentListParam[currentLang] }
            "
            [tooltip]="'TOOLTIPS.EMPTY_ASSIGNMENT_LIST' | translate"
            [textPositionClass]="'text-center'"
          />
        </div>
      } @else {
        <div class="overview-assignment-list">
          @for (assignment of (projectAssignments$ | async)?.items; track assignment.id) {
            <article
              class="overview-assignment-card"
              [class.overview-assignment-card--archived]="assignment.isArchived"
              [ngClass]="
                'overview-assignment-card--' +
                (assignment.status | enumToKebabCase: assignmentStatuses)
              "
            >
              <div class="assignment-card-main">
                <div class="assignment-title-row">
                  <div class="assignment-title-box">
                    <a
                      [routerLink]="['assignments/', assignment.id]"
                      [queryParams]="assignmentBackQueryParams"
                      class="assignment-title-link"
                    >
                      {{ assignment.title }}
                    </a>
                    <span
                      class="assignment-priority-mark"
                      [ngClass]="assignmentPriorityBadgeClassMap[assignment.priority]"
                    >
                      <span class="assignment-priority-mark__dot"></span>
                      {{
                        "ASSIGNMENT_PRIORITIES." +
                          (assignment.priority
                            | enumKey: assignmentPriorities
                            | uppercase) | translate
                      }}
                    </span>
                  </div>

                  <span
                    class="assignment-status-badge"
                    [ngClass]="
                      'status-' +
                      (assignment.status
                        | enumToKebabCase: assignmentStatuses)
                    "
                  >
                    {{
                      "ASSIGNMENT_STATUSES." +
                        (assignment.status
                          | enumKey: assignmentStatuses
                          | uppercase) | translate
                    }}
                  </span>
                </div>

                <div class="assignment-meta-grid">
                  <div class="assignment-meta-item">
                    <span class="assignment-meta-label">{{
                      "ASSIGNMENT_INFO.DEADLINE" | translate
                    }}</span>
                    <span>{{
                      assignment.deadline | date: uaDateFormat
                    }}</span>
                  </div>

                  <div class="assignment-meta-item">
                    <span class="assignment-meta-label">{{
                      "ASSIGNMENT_INFO.CREATED" | translate
                    }}</span>
                    <span>{{
                      assignment.createdAt | date: uaDateWithTimeFormat
                    }}</span>
                  </div>

                  <div class="assignment-meta-item">
                    <span class="assignment-meta-label">{{
                      "ASSIGNMENT_INFO.START_DATE" | translate
                    }}</span>
                    <span>{{
                      assignment.startDate | date: uaDateWithTimeFormat
                    }}</span>
                  </div>

                  @if (assignment.timeLeft) {
                    <div class="assignment-meta-item">
                      <span class="assignment-meta-label">{{
                        "ASSIGNMENT_INFO.TIME_LEFT" | translate
                      }}</span>
                      <span class="assignment-time-left">{{ assignment.timeLeft }}</span>
                    </div>
                  }
                  <div class="assignment-meta-item assignment-meta-item--performer">
                    <span class="assignment-meta-label">{{
                      "TABLE_HEADERS.PERFORMER" | translate
                    }}</span>

                    @if (
                      assignment.employeeId &&
                      assignmentPerformerProfiles[assignment.employeeId];
                      as performerProfile
                    ) {
                      <app-minimal-profile-info
                        class="assignment-performer-profile"
                        [profile]="performerProfile"
                        imageWidth="34px"
                        imageHeight="34px"
                        fontSize="12px"
                      />
                    } @else {
                      <span class="assignment-performer-empty">
                        {{ "COMPONENTS.PROJECTS.UNASSIGNED" | translate }}
                      </span>
                    }
                  </div>
                </div>

                @if (assignment.description) {
                  <p class="assignment-description">
                    {{ assignment.description | truncateHtml | escapeChars }}
                  </p>
                }
              </div>

              <div class="assignment-card-actions">
                <a
                  [routerLink]="['assignments/', assignment.id]"
                  [queryParams]="assignmentBackQueryParams"
                  class="overview-btn overview-btn-secondary"
                >
                  {{ "BUTTONS.OPEN_ASSIGNMENT" | translate }}
                </a>

                @if (!assignment.isArchived) {
                  <button
                    type="button"
                    class="overview-btn overview-btn-secondary"
                    (click)="onArchive(assignment.id)"
                  >
                    {{ "BUTTONS.ARCHIVE" | translate }}
                  </button>
                } @else {
                  <button
                    type="button"
                    class="overview-btn overview-btn-secondary"
                    (click)="onUnarchive(assignment.id)"
                  >
                    {{ "BUTTONS.UNARCHIVE" | translate }}
                  </button>
                }

                <button
                  type="button"
                  class="overview-btn overview-btn-danger"
                  (click)="deleteAssignment(project.id, assignment.id)"
                >
                  {{ "BUTTONS.DELETE_ASSIGNMENT" | translate }}
                </button>
              </div>
            </article>
          }
        </div>
      }

      <app-paginator
        class="overview-paginator"
        [totalItems]="(projectAssignments$ | async)?.totalCount || 0"
        [pageSize]="(projectAssignments$ | async)?.pageSize || 0"
        (pageChange)="onPageChange($event)"
        (pageSizeChange)="onChangePageSize($event)"
      />

      <app-reusable-modal
        [isOpen]="isCreateAssignmentModalOpen"
        [panelClass]="'max-w-6xl'"
        [contentScoped]="true"
        [title]="'BUTTONS.ADD_ASSIGNMENT' | translate"
        [subtitle]="'COMPONENTS.PROJECT_OVERVIEW.CREATE_ASSIGNMENT_MODAL_SUBTITLE' | translate"
        (closed)="closeCreateAssignmentModal()"
      >
        <app-project-create-assignment
          [companyId]="companyId"
          [projectId]="project.id"
          (cancelled)="closeCreateAssignmentModal()"
          (created)="onAssignmentCreated($event)"
        />
      </app-reusable-modal>

      <app-reusable-modal
        [isOpen]="isFilesModalOpen"
        [panelClass]="'max-w-6xl'"
        [borderless]="true"
        [contentScoped]="true"
        [title]="'BUTTONS.FILES' | translate"
        [subtitle]="'COMPONENTS.PROJECT_OVERVIEW.FILES_MODAL_SUBTITLE' | translate"
        (closed)="closeFilesModal()"
      >
        <div class="overview-files-modal-content">
          <app-project-files />
        </div>
      </app-reusable-modal>

      <app-reusable-modal
        [isOpen]="isEditProjectModalOpen"
        [panelClass]="'max-w-5xl'"
        [contentScoped]="true"
        [title]="'TITLES.EDIT_PROJECT' | translate"
        [subtitle]="'COMPONENTS.PROJECTS.EDIT_MODAL.SUBTITLE' | translate"
        (closed)="closeEditProjectModal()"
      >
        @if (editingProject; as projectToEdit) {
          <app-edit-project
            [companyId]="companyId"
            [project]="projectToEdit"
            (cancelled)="closeEditProjectModal()"
            (saved)="onEditProjectSave($event)"
          />
        }
      </app-reusable-modal>
    </div>
  </div>
}
